import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLabels, usePrefs, useRequestData, useFlags } from 'wsm-common-data';
import { hasUrlParam, setUrlParam } from 'wsm-srp-utilities';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import { isDealerImage } from '../utilities/images';
import { publishVehicleData } from '../utilities/vehicleTracking';

const VehicleImage = ({
	image,
	imageIndex,
	vehicleImageObs,
	enableImage,
	onImageLoad,
	placeholderHeight,
	link,
	uuid
}) => {
	const { windowId, widgetName } = useRequestData();
	const {
		altAttributeBefore,
		altAttributeAfter,
		titleAttributeBefore,
		titleAttributeAfter
	} = usePrefs();
	const labels = useLabels();
	const flags = useFlags();
	const onDemandLoad = flags['srp-on-demand-card-loading'];
	const IMAGE_WIDTH = 520;
	const PLACEHOLDER_WIDTH = 52;
	const imageRef = useRef();
	const [isInitialLoad, setIsInitialLoad] = useState(false);
	const [placeholderStyles, setPlaceholderStyles] = useState(null);
	// TODO delete native lazy loading logic after onDemandLoad FF is removed.
	// bool indicating if browser supports native lazy loading
	const canNativeLazyLoad = 'loading' in HTMLImageElement.prototype;
	// don't lazy load images that may be above the fold, or images inside the carousel that are not visible
	const shouldNativeLazyLoad =
		canNativeLazyLoad && imageIndex === 0 && !onDemandLoad;

	// used to transition from low to hi res images when native lazy loading is unavailable
	const enableProgressiveLoad =
		imageIndex === 0 && !canNativeLazyLoad && vehicleImageObs;
	let imageElement;
	if (enableProgressiveLoad && imageRef && imageRef.current) {
		imageElement = imageRef.current;
		vehicleImageObs.observe(imageElement);
	}

	const { uri, alt, title } = image;

	const imageUrl = isDealerImage(uri)
		? setUrlParam(uri, 'impolicy=downsize_bkpt&imdensity=1')
		: uri;
	const isOptimized =
		hasUrlParam(imageUrl, 'impolicy') && !hasUrlParam(uri, 'impolicy');
	let src;
	// TODO delete this first when ondemand loading FF is removed
	let datasrc;
	if (isOptimized && enableProgressiveLoad) {
		src = setUrlParam(imageUrl, `w=${PLACEHOLDER_WIDTH}`);
		datasrc = setUrlParam(imageUrl, `w=${IMAGE_WIDTH}`);
	} else if (isOptimized) {
		src = setUrlParam(imageUrl, `w=${IMAGE_WIDTH}`);
	} else {
		src = imageUrl;
	}

	useEffect(() => {
		if (isInitialLoad) {
			setPlaceholderStyles(null);
		} else if (placeholderHeight) {
			setPlaceholderStyles({
				height: placeholderHeight,
				overflow: 'hidden',
				padding: 0
			});
		}
	}, [isInitialLoad, placeholderHeight]);

	useEffect(() => {
		if (vehicleImageObs && imageElement) {
			return () => vehicleImageObs.unobserve(imageElement);
		}
		return () => {};
	}, [vehicleImageObs, imageElement]);

	if (enableImage && image) {
		const onLoad = () => {
			if (imageRef.current.src) {
				setIsInitialLoad(true);
				onImageLoad(imageRef);
			}
		};

		return (
			<a
				href={link}
				onClick={() => {
					trackEvent(widgetName, windowId, {
						element: 'Vehicle photo',
						result: 'Navigated to VDP'
					});
					publishVehicleData(uuid);
				}}
			>
				<div
					className={setClassNames([
						'img-container',
						'img-placeholder',
						isInitialLoad ? 'img-loaded' : null
					])}
					style={placeholderStyles}
				>
					<img
						loading={shouldNativeLazyLoad ? 'lazy' : null}
						ref={imageRef}
						alt={setClassNames([
							labels.get(altAttributeBefore),
							alt,
							labels.get(altAttributeAfter)
						])}
						title={setClassNames([
							labels.get(titleAttributeBefore),
							title,
							labels.get(titleAttributeAfter)
						])}
						onLoad={onLoad}
						onError={onLoad}
						className="w-100"
						src={src}
						data-src={datasrc}
					/>
				</div>
			</a>
		);
	}

	return null;
};

VehicleImage.propTypes = {
	image: PropTypes.shape({
		uri: PropTypes.string,
		alt: PropTypes.string,
		title: PropTypes.string
	}).isRequired,
	imageIndex: PropTypes.number.isRequired,
	enableImage: PropTypes.bool,
	onImageLoad: PropTypes.func,
	placeholderHeight: PropTypes.number,
	link: PropTypes.string,
	vehicleImageObs: PropTypes.shape({
		observe: PropTypes.func,
		unobserve: PropTypes.func
	}),
	uuid: PropTypes.string.isRequired
};

VehicleImage.defaultProps = {
	enableImage: false,
	onImageLoad: () => null,
	placeholderHeight: null,
	link: '',
	vehicleImageObs: null
};

export default VehicleImage;
